import { UploadOutlined } from '@ant-design/icons';
import { Button, Checkbox, Input, Select, Space, Upload, message } from 'antd';
import { Rule } from 'antd/lib/form';
import { UploadFile } from 'antd/lib/upload/interface';
import { useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { MAX_SIZE_2MB } from '../consts';
import useOnChangeField from '../hooks/useOnChangeField';
import { AttachmentDetail, SerialID, UUID } from '../types';
import Form from './Form';
import EditorCharCounter from './RichEditor/EditorCharCounter';
import RichEditor from './RichEditor/RichEditor';

interface EmailFormTemplate {
  id: UUID | SerialID;
  name: string;
  message: string;
  subject?: string;
  fromName?: string;
  attachments?: SerialID[];
}

interface EmailFormCustomVariable {
  value: string;
  label: string;
}

interface EmailFormProps {
  templates?: EmailFormTemplate[];
  customVariables?: EmailFormCustomVariable[];
  richEditorRules?: Rule[];
  image?: boolean;
  attachments?: boolean;
  maxMessageLength?: number;
  staticSubject?: boolean;
  fromName?: boolean;
  replyTo?: boolean;
  draft?: boolean;
  namePrefix?: (string | number)[];
  readOnly?: boolean;
  aiItem?: React.ReactNode;
  hideTemplateSelection?: boolean;
}

export interface EmailFormValues {
  subject: string;
  message: string;
  email_communication_template: SerialID | null;
  attachments: UploadFile[];
  fromName: string;
  replyTo?: string;
  enableReplyTo?: boolean;
}

const mimeTypes = {
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  doc: 'application/msword',
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  png: 'image/png',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
};

export function fileTypeToMimeType(extension: string): string | undefined {
  return mimeTypes[extension as keyof typeof mimeTypes];
}

export const supportedFiles = [
  '.doc',
  '.docx',
  '.pdf',
  '.jpg',
  '.png',
  '.jpeg',
  '.webp',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'image/png',
  'image/jpeg',
  'image/webp',
];

const EmailForm = ({
  templates,
  customVariables,
  image,
  maxMessageLength,
  attachments,
  richEditorRules,
  staticSubject,
  fromName,
  replyTo,
  draft,
  namePrefix = [],
  aiItem = null,
  readOnly = false,
  hideTemplateSelection = false,
}: EmailFormProps) => {
  const { t } = useTranslation();

  const form = Form.useFormInstance();
  const messageValue = Form.useWatch([...namePrefix, 'message'], form);

  const emailCommunicationTemplateValue = Form.useWatch(
    [...namePrefix, 'email_communication_template'],
    form
  );
  const enableReplyToValue = Form.useWatch([...namePrefix, 'enableReplyTo'], form);
  const queryClient = useQueryClient();

  useOnChangeField<string>(
    [...namePrefix, 'message'],
    useCallback(
      (message, _, form, contextNamePrefix) => {
        const template = templates?.find(
          (template) => template.id === emailCommunicationTemplateValue
        );

        if (!template) {
          return;
        }

        if (template?.message !== message) {
          form.setFieldValue(
            [...contextNamePrefix, ...namePrefix, 'email_communication_template'],
            null
          );
        }
      },
      [namePrefix, emailCommunicationTemplateValue, templates]
    )
  );

  useOnChangeField<string>(
    [...namePrefix, 'subject'],
    useCallback(
      (subject, _, form, contextNamePrefix) => {
        if (!staticSubject) {
          const template = templates?.find(
            (template) => template.id === emailCommunicationTemplateValue
          );

          if (!template) {
            return;
          }

          if (template?.subject !== subject) {
            form.setFieldValue(
              [...contextNamePrefix, ...namePrefix, 'email_communication_template'],
              null
            );
          }
        }
      },
      [staticSubject, templates, emailCommunicationTemplateValue, namePrefix]
    )
  );

  useOnChangeField<string>(
    [...namePrefix, 'fromName'],
    useCallback(
      (value, _, form, contextNamePrefix) => {
        if (fromName) {
          const template = templates?.find(
            (template) => template.id === emailCommunicationTemplateValue
          );

          if (!template) {
            return;
          }

          if (template?.fromName !== value) {
            form.setFieldValue(
              [...contextNamePrefix, ...namePrefix, 'email_communication_template'],
              null
            );
          }
        }
      },
      [fromName, templates, emailCommunicationTemplateValue, namePrefix]
    )
  );

  useOnChangeField<UploadFile[] | undefined>(
    [...namePrefix, 'attachments'],
    useCallback(
      (value, _, form, contextNamePrefix) => {
        if (attachments) {
          const template = templates?.find(
            (template) => template.id === emailCommunicationTemplateValue
          );

          if (!template) {
            return;
          }

          if (value && template?.attachments?.length !== value.length) {
            form.setFieldValue(
              [...contextNamePrefix, ...namePrefix, 'email_communication_template'],
              null
            );
          }
        }
      },
      [attachments, namePrefix, emailCommunicationTemplateValue, templates]
    )
  );

  useOnChangeField<string>(
    [...namePrefix, 'email_communication_template'],
    useCallback(
      async (emailCommunicationTemplate, _, form, contextNamePrefix) => {
        if (emailCommunicationTemplate) {
          const template = templates?.find(
            (template) => template.id === emailCommunicationTemplate
          );

          if (!template) {
            return;
          }

          const templateAttachments: UploadFile[] = [];

          if (attachments) {
            for (const attachmentId of template?.attachments || []) {
              const attachment = await queryClient.fetchQuery<AttachmentDetail>(
                `/attachments/${attachmentId}/`
              );
              templateAttachments.push({
                uid: attachment.id.toString(),
                name: attachment.filename,
                status: 'done',
                url: attachment.file,
                type: attachment.mimetype,
              });
            }
          }

          form.setFieldValue([...contextNamePrefix, ...namePrefix, 'message'], template.message);

          if (!staticSubject) {
            form.setFieldValue([...contextNamePrefix, ...namePrefix, 'subject'], template.subject);
          }
          if (fromName) {
            form.setFieldValue(
              [...contextNamePrefix, ...namePrefix, 'fromName'],
              template.fromName
            );
          }
          if (attachments) {
            form.setFieldValue(
              [...contextNamePrefix, ...namePrefix, 'attachments'],
              templateAttachments
            );
          }
        }
      },
      [attachments, fromName, namePrefix, queryClient, staticSubject, templates]
    )
  );

  return (
    <>
      <Form.Item
        label={t('Template de E-mail')}
        name={[...namePrefix, 'email_communication_template']}
        hidden={hideTemplateSelection}
      >
        <Select placeholder={t('Templates')}>
          <Select.Option value={null}>
            <Trans>Personalizado</Trans>
          </Select.Option>
          {templates?.map((item) => (
            <Select.Option value={item.id} key={item.id}>
              {item.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item
        label={t('Assunto')}
        name={[...namePrefix, 'subject']}
        rules={[{ required: !staticSubject && !draft }]}
      >
        <Input placeholder={t('Retorno do processo seletivo')} disabled={staticSubject} />
      </Form.Item>
      <Form.Row>
        {fromName && (
          <Form.Col span={replyTo ? 12 : 24}>
            <Form.Item
              label={t('Enviado por')}
              name={[...namePrefix, 'fromName']}
              rules={[{ required: !draft }]}
            >
              <Input />
            </Form.Item>
          </Form.Col>
        )}
        {replyTo && (
          <Form.Col span={fromName ? 12 : 24}>
            <Form.Item
              label={
                <Space>
                  <Trans>Responder para</Trans>{' '}
                  <Form.Item
                    noStyle
                    name={[...namePrefix, 'enableReplyTo']}
                    valuePropName="checked"
                  >
                    <Checkbox>
                      <Trans>Habilitar</Trans>
                    </Checkbox>
                  </Form.Item>
                </Space>
              }
              name={[...namePrefix, 'replyTo']}
              rules={[{ type: 'email', message: t('Email inválido') }]}
            >
              <Input disabled={!enableReplyToValue} />
            </Form.Item>
          </Form.Col>
        )}
      </Form.Row>
      {aiItem}
      <Form.Item label={t('Conteúdo')}>
        <Form.Item
          noStyle
          name={[...namePrefix, 'message']}
          rules={[
            {
              validator(_, value) {
                if (!maxMessageLength || value.length <= maxMessageLength) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error(
                    t(
                      `A mensagem não pode ultrapassar {{maxMessageLength}} caracteres. O contador de caracteres também contabiliza tags HTML (por exemplo, <p></p>) conforme a formatação escolhida`,
                      { maxMessageLength }
                    )
                  )
                );
              },
            },
            ...(richEditorRules || []),
          ]}
        >
          <RichEditor
            image={image}
            video={false}
            variables={customVariables}
            data-testid="rich-editor-input"
            readOnly={readOnly}
          />
        </Form.Item>
        {maxMessageLength && (
          <EditorCharCounter count={messageValue?.length || 0} limit={maxMessageLength} />
        )}
      </Form.Item>
      {attachments && (
        <Form.Item
          name={[...namePrefix, 'attachments']}
          valuePropName="fileList"
          getValueFromEvent={(e) => {
            if (Array.isArray(e)) {
              return e;
            }
            return e && e.fileList;
          }}
        >
          <Upload
            listType="text"
            accept={supportedFiles.join(',')}
            beforeUpload={(file: UploadFile) => {
              if (fileTypeToMimeType(file.name.split('.').pop()?.toLowerCase() || '')) {
                if (!file.size) {
                  message.error(t('Erro com o arquivo.'));
                  return;
                }
                if (file.size >= MAX_SIZE_2MB) {
                  message.error(
                    t(
                      'O arquivo selecionado excede o limite de 2 MB. Por favor, envie um arquivo menor.'
                    )
                  );
                  return Upload.LIST_IGNORE;
                }
                return false;
              } else {
                message.error(
                  t(
                    'Por favor, selecione um arquivo no formato ".pdf", ".png", ".jpg", ".jpeg", "webp", ".doc" ou ".docx".'
                  )
                );
                return Upload.LIST_IGNORE;
              }
            }}
          >
            <Button icon={<UploadOutlined />}>Upload</Button>
          </Upload>
        </Form.Item>
      )}
    </>
  );
};

export default EmailForm;
